import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import catalogo from '../datos/catalogo.json';
import guiones from '../datos/guiones-journey2.json';
import { createResolver } from '../engine/resolve';
import { loadCase, resolveGo, paqueteAsesor, nextLinear } from '../engine/machine';
import { session, saveSession, loadLatestSession, resetSession } from '../engine/store';
import { pdfPoliza, pdfCondiciones, pdfPlanPagos, primaCaso } from '../engine/pdf';

import { Header } from '../components/Header';
import { useMarca } from '../engine/marcas';
import { Bubble, Typing } from '../components/Bubble';
import { DataCard } from '../components/DataCard';
import { Comparator } from '../components/Comparator';
import { Checklist } from '../components/Checklist';
import { Perfilador } from '../components/Perfilador';
import { CierrePago } from '../components/CierrePago';
import { agendarReal } from '../lib/agenda';
import { ComparadorMVP } from '../components/ComparadorMVP';
import { AsesorSheet } from '../components/AsesorSheet';
import { VozLlamada } from '../components/VozLlamada';
import { opcionesComparador, productoMvp, nombreProducto, previasDeLead } from '../engine/perfil';
import { llano } from '../engine/llano';
import { Opts } from '../components/Opts';
import { AdvisorPanel } from '../components/AdvisorPanel';
import { MAX_CUOTAS, planCuotas, fechaCorta } from '../engine/cuotas';
import { EvidenceLog } from '../components/EvidenceLog';
import { ProtectionRing } from '../components/ProtectionRing';
import { IconAdvisor, IconDoc, IconPlay, IconReset, IconArrowRight, IconChat } from '../components/icons';

// Pago en cuotas: el cliente elige de 1 a 11 (sesión #6); valor y fecha de cada cuota en engine/cuotas


const STRIKES = ['Lo que ya pagas', 'Momento', 'Simulador', 'Antes de seguir', 'Comparador', 'Asesor', 'Lo que firmas', 'Si pasa algo'];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Cadencia de lectura (Fase 9): "escribiendo" proporcional al largo del mensaje, más humano.
// Ritmo: 1,5 s antes de un mensaje corto; 2,7 s antes de una tabla, tarjeta o comparador; botones 0,5 s después.
const TYPING_MIN = 650;
const typingTime = (txt) => (String(txt || '').replace(/<[^>]+>/g, '').length > 140 ? 2000 : 1500);
const CARD_TYPING_MS = 2700;
const OPTS_DELAY_MS = 500;
const BUBBLE_GAP_MS = 450;
const CARD_MS = 900;
const AUTO_MS = 1200;
const ANCLAS = ['card', 'check', 'cmpmvp'];

// Punto 28 · Modo libre: en el texto visible NO se muestran códigos de fila/regla
// (MKT-xxx, QA-xxx, Rxx, "fila …", "regla R#", "base de contratación"). Van solo al log y a Vista Junta.
const stripCodes = (t) =>
  String(t || '')
    .replace(/,?\s*fila\s+MKT-\d+/gi, '')
    .replace(/,?\s*regla\s+R\d+/gi, '')
    .replace(/\bbase de contrataci[oó]n\b/gi, '')
    .replace(/\b(?:MKT-\d+|QA-\d+|R\d+)\b/g, '')
    .replace(/\(\s*[·,;\s]*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:])/g, '$1')
    .trim();

// Fragmento de siniestro (strike 7, solo caso A). Editable · default on.
// Simula 30 s: pide fotos, abre el caso y da un número. No resuelve el siniestro.
const FRAGMENTO_SINIESTRO = true;

export default function Conversation({ caseId: propCaseId, auto = false, wa = false, resume = false, onHome }) {
  const [marca] = useMarca();
  // Marca blanca: el texto fijo "MBM" se muestra con el nombre de la entidad activa.
  const marcaTxt = useCallback(
    (s) => (marca.id === 'mbm' ? s : String(s == null ? '' : s).replace(/\bMBM\b/g, marca.nombre)),
    [marca],
  );
  // AC12: overrides de prima solo en memoria (session.catalogOverrides); recargar vuelve al catálogo
  const [ovTick, setOvTick] = useState(0);
  const resolver = useMemo(() => createResolver(catalogo, session.catalogOverrides || {}), [ovTick]);

  // Retoma (Fase 8): reabre la última sesión guardada por WhatsApp donde quedó
  const resumeRef = useRef(null);
  if (resumeRef.current === null) {
    resumeRef.current = resume ? loadLatestSession() || false : false;
    if (resumeRef.current && resumeRef.current.lead) session.lead = resumeRef.current.lead;
  }
  const resumed = resumeRef.current || null;

  const [waMode, setWaMode] = useState(wa);

  // fromJ1 es un handoff de una sola vez: solo "Seguir por chat" lo activa;
  // abrir #A/#B/#C directo entra por el flujo normal. La sesión guardada no se inyecta sola.
  const handoffRef = useRef(null);
  if (handoffRef.current === null) {
    handoffRef.current = session.fromJ1 === true;
    session.fromJ1 = false;
    session.autoContinue = false;
  }
  const fromJ1 = handoffRef.current;
  const caseId = resumed && ['A', 'B', 'C'].includes(resumed.caso)
    ? resumed.caso
    : (['A', 'B', 'C', 'V', 'S'].includes(propCaseId) ? propCaseId : 'A');
  const mc = useMemo(() => loadCase(guiones, caseId), [caseId]);
  // fromJ1: entra por el primer paso con strike 0 y card; se omite la pregunta abierta del Strike 1
  const startIdx = useMemo(() => {
    if (resumed) return Math.min(Math.max(resumed.paso || 0, 0), mc.steps.length - 1);
    if (!fromJ1) return 0;
    const i = mc.steps.findIndex((s) => s.inicio || (s.strike === 0 && s.card));
    return i === -1 ? 0 : i;
  }, [fromJ1, resumed, mc]);
  const openQIdx = useMemo(() => mc.steps.findIndex((s) => s.strike === 1 && !s.perfil), [mc]);

  const [events, setEvents] = useState([]);
  const [pfPend, setPfPend] = useState(null); // S11: pregunta del perfil con opciones
  const agendaRef = useRef(null); // cita real en Calendar (se llena más abajo)
  // S5 · «Atrás»: pila de pasos con el largo del chat al empezar cada uno
  const eventsRef = useRef([]);
  const histRef = useRef([]);
  useEffect(() => { eventsRef.current = events; }, [events]);
  const [log, setLog] = useState(() => (resumed ? (resumed.log || []) : (fromJ1 ? session.j1log.filter(Boolean) : [])));
  const [opts, setOpts] = useState(null);
  const [stepIdx, setStepIdx] = useState(null);
  const [strike, setStrike] = useState(0);
  const [checks, setChecks] = useState([]);
  const [advisor, setAdvisor] = useState(null);
  const [advisorLock, setAdvisorLock] = useState(false);
  const [ended, setEnded] = useState(false);
  const [running, setRunning] = useState(false);
  const [autoOn, setAutoOn] = useState(auto);
  const [showLog, setShowLog] = useState(false);
  const [fuente, setFuente] = useState(null);
  const [hl, setHl] = useState(null);
  const [sheet, setSheet] = useState(false);
  const [voz, setVoz] = useState(false);
  const [menu, setMenu] = useState(false);
  const [pfTexto, setPfTexto] = useState(null);
  const [cmpSel, setCmpSel] = useState({});
  const [pfTick, setPfTick] = useState(0);
  const [pfCmp, setPfCmp] = useState({});
  const etapas = mc.caso.etapas || STRIKES;
  // Punto 23: tono usted en casos B, C, Vida y Salud
  const usted = caseId !== 'A';
  // Variables del cliente + lenguaje llano (tabla del punto 0) en todo lo visible
  const vars = useCallback((t) => {
    const lead = session.lead || {};
    const pa = (lead.perfil && lead.perfil.autos && lead.perfil.autos.answers) || {};
    const nom = (lead.nombre || '').trim().split(' ')[0];
    return String(t == null ? '' : t)
      .replace(/,?\s*\{nombre\}/g, (m) => (nom ? m.replace('{nombre}', nom) : ''))
      .replace(/al \{whatsapp\}/g, lead.tel ? `al ${lead.tel}` : 'a tu WhatsApp')
      .replace(/\{carro\}/g, pa.carro || 'tu carro')
      .replace(/\{ciudad\}/g, pa.ciudad || 'tu ciudad');
  }, []);
  const txt = useCallback((t) => llano(vars(marcaTxt(resolver.tpl(t))), { usted }), [vars, marcaTxt, resolver, usted]);

  const keyRef = useRef(0);
  const genRef = useRef(0);
  const startedRef = useRef(false);
  const dudaRef = useRef(null);
  const scrollRef = useRef(null);
  const terminarRef = useRef(() => {});
  const autoRef = useRef(auto);
  autoRef.current = autoOn;
  const anclaRef = useRef(null);
  const k = (p) => `${p}${keyRef.current++}`;

  const pushLog = useCallback((s, extra = {}) => {
    setLog((lg) => [
      ...lg,
      {
        turno: lg.length + 1,
        journey: 2,
        strike: s.strike,
        agente: s.agente,
        regla: s.log.regla,
        fila: s.log.fila,
        dec: s.log.dec,
        txt: s.log.txt,
        ts: new Date().toISOString(),
        ...extra,
      },
    ]);
  }, []);

  const runStepRef = useRef(null);
  const runStep = useCallback(
    async (i) => {
      const gen = genRef.current;
      const s = mc.steps[i];
      setRunning(true);
      setOpts(null);
      if (!histRef.current.length || histRef.current[histRef.current.length - 1].step !== i) {
        histRef.current.push({ step: i, evLen: eventsRef.current.length });
      }
      setStepIdx(i);
      setStrike(s.strike ?? 0);
      if (s.asesor) {
        const pf = session.lead && session.lead.perfil && session.lead.perfil[mc.caso.ramo];
        const data = pf && pf.eleccion ? { ...s.asesor, prod: `${s.asesor.prod} · ${pf.eleccion}` } : s.asesor;
        setAdvisor({ data, canal: null });
      }

      const decir = async (lista) => {
        for (const b of lista || []) {
          const html = txt(b);
          const tk = k('t');
          setEvents((ev) => [...ev, { type: 'typing', key: tk }]);
          await sleep(typingTime(html));
          if (gen !== genRef.current) return false;
          setEvents((ev) => [
            ...ev.filter((e) => e.key !== tk),
            { type: 'bubble', from: 'bot', html, agente: s.agente, key: k('b') },
          ]);
          await sleep(BUBBLE_GAP_MS);
          if (gen !== genRef.current) return false;
        }
        return true;
      };
      const pausaTarjeta = async () => {
        const tk = k('t');
        setEvents((ev) => [...ev, { type: 'typing', key: tk }]);
        await sleep(CARD_TYPING_MS);
        setEvents((ev) => ev.filter((e) => e.key !== tk));
        return gen === genRef.current;
      };
      if (!(await decir(s.bot))) return;
      if (s.card) {
        if (!(await pausaTarjeta())) return;
        const card = {
          ...s.card,
          k: txt(s.card.k),
          rows: s.card.rows.map(([l, v]) => [txt(l), txt(v)]),
          src: llano(vars(resolver.tpl(s.card.src)), { usted, fuente: true }),
        };
        setEvents((ev) => [...ev, { type: 'card', card, key: k('c') }]);
        await sleep(CARD_MS);
        if (gen !== genRef.current) return;
      }
      if (s.comparar) {
        if (!(await pausaTarjeta())) return;
        setEvents((ev) => [...ev, { type: 'cmpmvp', ramo: s.comparar, step: i, key: k('cm') }]);
      }
      if (!(await decir(s.bot2))) return;
      if (s.cmp) {
        setEvents((ev) => [...ev, { type: 'cmp', ids: s.cmp, key: k('m') }]);
        await sleep(CARD_MS);
        if (gen !== genRef.current) return;
      }
      if (s.perfil) setEvents((ev) => [...ev, { type: 'perfil', ramo: s.perfil, step: i, key: k('pf') }]);
      if (s.check) {
        // Solo las reglas que aplican según lo que respondió (5º elemento: condición sobre el perfil)
        const pf = (session.lead && session.lead.perfil) || {};
        const items = s.check
          .filter((it) => !it[4] || ((pf[it[4].perfil] && pf[it[4].perfil].answers) || {})[it[4].id] === it[4].v)
          .map((it) => [txt(it[0]), it[1], txt(it[2] || ''), txt(it[3] || '')]);
        if (!(await pausaTarjeta())) return;
        setChecks(items.map(() => false));
        setEvents((ev) => [...ev, {
          type: 'check', items, step: i, key: k('k'),
          titulo: s.checkTitulo ? txt(s.checkTitulo.replace('{n}', items.length)) : null,
          sub: s.checkSub ? txt(s.checkSub) : null,
        }]);
      } else {
        setChecks([]);
      }
      if (s.asesor) {
        setEvents((ev) => [...ev, { type: 'advisor', key: k('h') }]);
        await sleep(TYPING_MIN);
        if (gen !== genRef.current) return;
      }
      if (s.sys) setEvents((ev) => [...ev, { type: 'sys', text: txt(s.sys), key: k('s') }]);
      if (s.log) pushLog(s);
      if (s.fin) {
        setRunning(false);
        terminarRef.current();
        return;
      }
      // Fragmento de siniestro: si está apagado, se oculta la opción de simular el choque (solo caso A)
      let salidas = (s.opts || []).map((o) => ({ ...o, t: llano(vars(marcaTxt(o.t)), { usted }) }));
      if (!FRAGMENTO_SINIESTRO) salidas = salidas.filter((o) => o.go !== 'a_sin');
      await sleep(OPTS_DELAY_MS);
      if (gen !== genRef.current) return;
      setOpts(s.perfil || (s.comparar && autoRef.current) ? null : salidas);
      setRunning(false);
    },
    [mc, resolver, pushLog, marcaTxt, txt, vars, usted],
  );

  // S5 · vuelve al paso anterior: borra lo que vino después y lo repite, para cambiar la respuesta
  const atras = useCallback(() => {
    const h = histRef.current;
    if (h.length < 2) return;
    genRef.current += 1;
    h.pop();
    const prev = h.pop();
    setEvents((ev) => ev.slice(0, prev.evLen));
    eventsRef.current = eventsRef.current.slice(0, prev.evLen);
    setOpts(null);
    setPfTexto(null);
    setEnded(false);
    setRunning(false);
    setCmpSel((c) => Object.fromEntries(Object.entries(c).filter(([s]) => Number(s) < prev.step)));
    if (!mc.steps.slice(0, prev.step + 1).some((s) => s.asesor)) setAdvisor(null);
    runStepRef.current(prev.step);
  }, [mc]);

  runStepRef.current = runStep;
  const terminar = useCallback(() => {
    setEnded(true);
    setAutoOn(false);
    setOpts(null);
    setPfTexto(null);
  }, []);
  terminarRef.current = terminar;

  const choose = useCallback(
    async (opt) => {
      if (running || ended) return;
      const gen = genRef.current;
      const i = stepIdx;
      setOpts(null);
      const sCur = mc.steps[i];
      if (sCur && sCur.guarda) session.lead = { ...(session.lead || {}), [sCur.guarda]: opt.t };
      // La forma de pago y las cuotas que elige en el chat llegan al link de pago y al plan de pagos
      if (sCur && sCur.guarda === 'forma_pago') setPay((p) => ({ ...p, plan: /contado/i.test(opt.t) ? 'contado' : 'cuotas' }));
      if (sCur && sCur.guarda === 'cuotas') setPay((p) => ({ ...p, cuotas: Math.min(parseInt(opt.t, 10) || MAX_CUOTAS, MAX_CUOTAS) }));
      // Punto 18: si esta opción es el canal/horario que ya se muestra en "Canal y hora",
      // no pintamos además la burbuja del usuario con el mismo texto.
      const settingCanal = advisor && !advisor.canal;
      if (!settingCanal) {
        setEvents((ev) => [...ev, { type: 'bubble', from: 'user', html: opt.t.replace(/^📎\s*/, ''), key: k('u') }]);
      } else {
        setAdvisor((a) => (a ? { ...a, canal: opt.t } : a));
        if (agendaRef.current) agendaRef.current(/llamada/i.test(opt.t) ? 'Llamada' : 'WhatsApp', opt.t);
      }
      await sleep(320);
      if (gen !== genRef.current) return;
      let next = resolveGo(mc, i, opt);
      if (fromJ1 && next === openQIdx) next = nextLinear(mc, next); // omite la pregunta del Strike 1
      if (next === -1 || next == null) return terminar();
      runStep(next);
    },
    [running, ended, stepIdx, mc, advisor, runStep, terminar, fromJ1, openQIdx],
  );

  const perfilAnswers = (ramo) => {
    const pf = session.lead && session.lead.perfil && session.lead.perfil[ramo];
    return (pf && pf.answers) || {};
  };

  // Respuestas parciales: el asesor recibe lo respondido hasta ese punto
  const perfilParcial = useCallback((r) => {
    session.lead = { ...(session.lead || {}), perfil: { ...((session.lead && session.lead.perfil) || {}), [r.ramo]: r } };
  }, []);

  // Comparador como paso propio (caso A): elegir sigue al resumen y a agendar
  const elegirCmp = useCallback(async (prod, ids, i) => {
    const gen = genRef.current;
    const ramo = mc.steps[i].comparar;
    const nombres = ids.map((id) => nombreProducto(productoMvp(id)));
    const eleccion = nombreProducto(prod);
    setCmpSel((c) => ({ ...c, [i]: prod.id }));
    setOpts(null);
    const prev = (session.lead && session.lead.perfil && session.lead.perfil[ramo]) || { ramo, respuestas: [], answers: {} };
    session.lead = { ...(session.lead || {}), perfil: { ...((session.lead && session.lead.perfil) || {}), [ramo]: { ...prev, eleccion, opciones: nombres } } };
    setLog((lg) => [...lg, {
      turno: lg.length + 1, journey: 2, strike: mc.steps[i].strike, agente: 'Comparador', regla: 'Elección del cliente',
      fila: 'productos-mvp', dec: 'pasa', txt: `elección: ${eleccion}`, ts: new Date().toISOString(),
    }]);
    setEvents((ev) => [...ev, { type: 'bubble', from: 'user', html: `Quiero esta opción: ${eleccion}`, key: k('u') }]);
    await sleep(320);
    if (gen !== genRef.current) return;
    const next = nextLinear(mc, i);
    if (next === -1) terminarRef.current();
    else runStep(next);
  }, [mc, runStep]);

  // Perfil del caso: se guarda en el lead; elegir o pedir asesor sigue al paso del asesor
  const perfilListo = useCallback(
    async (r, i) => {
      const gen = genRef.current;
      setOpts(null);
      session.lead = { ...(session.lead || {}), perfil: { ...((session.lead && session.lead.perfil) || {}), [r.ramo]: r } };
      setLog((lg) => [
        ...lg,
        {
          turno: lg.length + 1, journey: 2, strike: mc.steps[i].strike, agente: 'Perfilador',
          regla: r.escalado ? 'Escala por perfil' : 'Perfil del caso',
          fila: 'productos-mvp', dec: r.escalado ? 'escala' : 'pasa',
          txt: `${r.respuestas.map(([l, v]) => `${l}: ${v}`).join(' · ')}${r.eleccion ? ` · elección: ${r.eleccion}` : ''}`,
          ts: new Date().toISOString(),
        },
      ]);
      if (r.eleccion) {
        const t = r.eleccion === 'Prefiere hablar con un asesor' ? 'Prefiero hablar con un asesor' : `Quiero esta opción: ${r.eleccion}`;
        setEvents((ev) => [...ev, { type: 'bubble', from: 'user', html: t, key: k('u') }]);
      }
      await sleep(r.escalado ? 900 : 320);
      if (gen !== genRef.current) return;
      // S13: plan cerrado dentro de los límites → cierre con pago, sin pasar al asesor
      if (r.plan && r.plan.cerrado && !r.escalado) {
        setEvents((ev) => [
          ...ev,
          { type: 'bubble', from: 'bot', html: 'Listo. Como es un plan cerrado y está dentro de lo que su perfil permite, lo puede tomar ya, sin esperar a un asesor.', key: k('b') },
          { type: 'pago', plan: r.plan, answers: r.answers, step: i, key: k('pago') },
        ]);
        return;
      }
      const next = r.escalado ? (mc.byId.a_plat ?? nextLinear(mc, i)) : nextLinear(mc, i);
      if (next === -1) return terminar();
      runStep(next);
    },
    [mc, runStep, terminar],
  );

  const sinCierre = caseId === 'V' || caseId === 'S';
  // «Hablar con un asesor»: abre el panel de canal y franja en cualquier momento del chat
  const pedirAsesor = useCallback(() => {
    if (ended && advisorLock) return;
    setAutoOn(false);
    setSheet(true);
  }, [ended, advisorLock]);

  // Cita real en el calendario (chat o voz): confirma en el chat cuando Calendar responde
  const agendarEnCalendario = useCallback(async (canal, franja, origen = 'chat', correo = '') => {
    const lead = session.lead || {};
    const pf = lead.perfil && lead.perfil[mc.caso.ramo || 'autos'];
    const r = await agendarReal({
      nombre: lead.nombre || 'Cliente de ejemplo', canal, franja, origen, correo,
      producto: (pf && pf.eleccion) || mc.caso.producto || '', resumen: mc.caso.nombre || '',
    });
    setEvents((ev) => [...ev, {
      type: 'sys', key: k('cal'),
      text: r.ok
        ? r.simulada
          ? `📅 Cita registrada para el asesor: ${r.texto}${r.movida ? ' (el primer espacio dentro del horario de atención)' : ''} · en esta demo no sale invitación real.`
          : `📅 Cita creada en el calendario del asesor: ${r.texto}${r.movida ? ' (el primer espacio dentro del horario de atención)' : ''} · la invitación ya salió${correo ? ', también al correo indicado' : ''}.`
        : 'No pude crear la cita en el calendario; el asesor la agenda a mano.',
    }]);
    setLog((lg) => [...lg, { turno: lg.length + 1, journey: 2, strike: 0, agente: 'Agenda (Calendar)', regla: 'Cita real', fila: '—', dec: r.ok ? 'pasa' : 'escala', txt: r.ok ? `Cita ${r.inicio} · invitados: ${(r.invitados || []).join(', ')}` : `Error: ${r.error}`, ts: new Date().toISOString() }]);
    return r;
  }, [mc]);

  agendaRef.current = agendarEnCalendario;

  const confirmarAsesor = useCallback((canal, [franjaT, franjaTxt], correo = '') => {
    genRef.current += 1;
    setSheet(false);
    setRunning(false);
    setOpts(null);
    setAdvisorLock(true);
    const pkg = paqueteAsesor(mc.caso);
    if (dudaRef.current) pkg.res = `${pkg.res} · Punto por aclarar: “${dudaRef.current}”`;
    const verbo = canal === 'Llamada' ? (usted ? 'lo llama' : 'te llama') : (usted ? 'le escribe' : 'te escribe');
    const cierre = usted ? 'Ya tiene lo que hablamos: no tendrá que repetir nada.' : 'Ya tiene lo que hablamos: no tendrás que repetir nada.';
    setAdvisor({ data: pkg, canal: `${canal} · ${franjaT}` });
    setEvents((ev) => [
      ...ev.filter((x) => x.type !== 'typing' && x.type !== 'advisor'),
      { type: 'bubble', from: 'user', html: `Quiero hablar con un asesor · ${canal}, ${franjaT}`, key: k('u') },
      { type: 'bubble', from: 'bot', html: vars(`Listo, {nombre}. Un asesor ${verbo} ${franjaTxt}. ${cierre}`), agente: 'Orquestador de desborde', key: k('p') },
      { type: 'advisor', key: k('p2') },
    ]);
    setLog((lg) => [
      ...lg,
      {
        turno: lg.length + 1, journey: 2, strike: stepIdx != null ? mc.steps[stepIdx].strike : 0,
        agente: 'Orquestador de desborde', regla: 'Desborde manual', fila: '—', dec: 'escala',
        txt: `pedido explícito · ${canal} · ${franjaT}`, ts: new Date().toISOString(),
      },
    ]);
    terminar();
    agendarEnCalendario(canal, franjaT, 'chat', correo);
  }, [mc, stepIdx, usted, vars, terminar, agendarEnCalendario]);

  // --- Modo libre (Fase 6) · campo de texto bajo las opciones, anclado a la base ---
  const [libreInput, setLibreInput] = useState('');
  const [libreBusy, setLibreBusy] = useState(false);
  const [pay, setPay] = useState({ open: false, plan: 'contado', cuotas: MAX_CUOTAS, ref: null });

  const pushLogLibre = useCallback(
    (q, meta) => {
      setLog((lg) => [
        ...lg,
        {
          turno: lg.length + 1,
          journey: 2,
          strike: stepIdx != null ? mc.steps[stepIdx].strike : 0,
          agente: 'Modo libre (anclado a la base)',
          regla: meta.regla || '—',
          fila: meta.fila || '—',
          dec: meta.dec || 'info',
          txt: `Pregunta libre: “${q}” · vía ${meta.modo || '—'}`,
          ts: new Date().toISOString(),
        },
      ]);
    },
    [mc, stepIdx],
  );

  const preguntarLibre = useCallback(
    async (e) => {
      e.preventDefault();
      const q = libreInput.trim();
      if (!q || libreBusy) return;
      if (pfTexto) {
        if (pfTexto.submit(q)) setLibreInput('');
        return;
      }
      const gen = genRef.current;
      setLibreBusy(true);
      setLibreInput('');
      const bkey = k('ml');
      setEvents((ev) => [
        ...ev,
        { type: 'bubble', from: 'user', html: q, key: k('q') },
        { type: 'typing', key: 'pensando' },
      ]);
      let acc = '';
      let started = false;
      let meta = null;
      const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      try {
        // S11 · contexto del turno: historial, pregunta pendiente y perfil
        const limpio = (h) => String(h || '').replace(/<[^>]+>/g, '').trim();
        const historial = eventsRef.current.filter((x) => x.type === 'bubble' && x.html).slice(-14)
          .map((x) => ({ de: x.from === 'user' ? 'cliente' : 'bot', t: limpio(x.html) }));
        const ultimaBot = [...historial].reverse().find((h) => h.de === 'bot');
        const pendiente = pfPend
          ? { pregunta: pfPend.pregunta, opciones: pfPend.opciones, multi: pfPend.multi }
          : (opts && opts.length ? { pregunta: ultimaBot ? ultimaBot.t : '', opciones: opts.map((o) => o.t), multi: false } : null);
        const pf = session.lead && session.lead.perfil && session.lead.perfil[mc.caso.ramo || 'autos'];
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/modo-libre`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pregunta: q, caso: caseId, session_id: `web-${Date.now()}`, historial, pendiente, perfil: (pf && pf.answers) || {} }),
        });
        const reader = res.body.getReader();
        const dec8 = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          if (gen !== genRef.current) return;
          buf += dec8.decode(value, { stream: true });
          const parts = buf.split('\n\n');
          buf = parts.pop();
          for (const part of parts) {
            const mEv = part.match(/^event: (\w+)/m);
            const mData = part.match(/^data: (.*)$/m);
            if (!mEv || !mData) continue;
            let payload = null;
            try { payload = JSON.parse(mData[1]); } catch (e2) { continue; }
            if (mEv[1] === 'tok') {
              acc += payload.t;
              if (!started) {
                started = true;
                setEvents((ev) => [
                  ...ev.filter((x) => x.key !== 'pensando'),
                  { type: 'bubble', from: 'bot', html: '', agente: 'Modo libre (anclado a la base)', key: bkey },
                ]);
              }
              const texto = esc(stripCodes(acc));
              setEvents((ev) => ev.map((x) => (x.key === bkey ? { ...x, html: texto } : x)));
            } else if (mEv[1] === 'meta') {
              meta = payload;
              if (payload.texto_final) {
                const tf = esc(stripCodes(payload.texto_final));
                acc = payload.texto_final;
                setEvents((ev) => ev.map((x) => (x.key === bkey ? { ...x, html: tf } : x)));
              }
            }
          }
        }
      } catch (err) {
        acc = 'Eso no está en mi base y no lo voy a improvisar. Te lo paso a un asesor con la pregunta tal cual la escribiste.';
        meta = { modo: 'error', dec: 'escala', regla: '—', fila: '—' };
        setEvents((ev) => [
          ...ev.filter((x) => x.key !== 'pensando'),
          { type: 'bubble', from: 'bot', html: acc, agente: 'Modo libre (anclado a la base)', key: bkey },
        ]);
        started = true;
      }
      if (!started) setEvents((ev) => ev.filter((x) => x.key !== 'pensando'));
      if (meta) {
        pushLogLibre(q, meta);
        if (meta.dec === 'escala') setEvents((ev) => [...ev, { type: 'libre-cta', key: k('lc') }]);
        // S11 · respondió la pregunta pendiente con sus palabras → se marca como si hubiera tocado la opción
        if (meta.accion === 'elegir_opcion' && (meta.opciones || []).length) {
          await sleep(600);
          if (pfPend) pfPend.elegir(meta.opciones);
          else {
            const o = (opts || []).find((x) => x.t === meta.opciones[0]);
            if (o) choose(o);
          }
        } else if (meta.retomar) {
          const pq = pfPend ? pfPend.pregunta : null;
          await sleep(900);
          setEvents((ev) => [...ev, { type: 'bubble', from: 'bot', html: pq ? `Volviendo a lo ${usted ? 'suyo' : 'tuyo'}: ${pq}` : `Volviendo a lo ${usted ? 'suyo' : 'tuyo'}: ${usted ? 'elija' : 'elige'} una de las opciones de abajo o ${usted ? 'escríbalo' : 'escríbelo'} con ${usted ? 'sus' : 'tus'} palabras.`, key: k('b') }]);
        }
      }
      setLibreBusy(false);
    },
    [libreInput, libreBusy, caseId, pushLogLibre, pfTexto, pfPend, opts, choose, usted, mc],
  );

  // Entrada desde el recorrido inicial: sin etiquetas internas
  const introJ1 = useCallback((p) => (caseId === 'A'
    ? [{ type: 'sys', text: vars('Seguimos con tu carro nuevo, {nombre}'), key: `${p}0` }]
    : [
      { type: 'sys', text: vars('Seguimos donde ibas, {nombre}'), key: `${p}0` },
      { type: 'bubble', from: 'bot', html: `Hola, soy el asistente virtual de ${marca.nombre}, no una persona.`, key: `${p}1` },
    ]), [caseId, vars, marca]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const intro = [];
    if (resumed) {
      const nom = (resumed.lead && resumed.lead.nombre) || '';
      const momRaw = (resumed.lead && resumed.lead.momento) || '';
      const mom = momRaw.includes('·') ? momRaw.split('·').slice(1).join('·').trim() : (momRaw || 'lo que veníamos mirando');
      intro.push({ type: 'sys', text: 'Retomamos tu conversación de WhatsApp donde quedó', key: k('re') });
      intro.push({ type: 'bubble', from: 'bot', html: marcaTxt(`Hola${nom ? ' ' + nom : ''}, soy el asistente virtual de ${marca.nombre}, no una persona. Retomamos lo de ${mom} donde lo dejamos.`), key: k('reb') });
    } else if (fromJ1) {
      intro.push(...introJ1('j1'));
    }
    if (intro.length) setEvents(intro);
    runStep(startIdx);
  }, [runStep, fromJ1, resumed, startIdx]);

  // ▶ Auto: primera opción, cada 1400 ms, determinista
  useEffect(() => {
    const s0 = stepIdx != null ? mc.steps[stepIdx] : null;
    if (autoOn && !ended && !running && s0 && s0.comparar && !cmpSel[stepIdx]) {
      const t0 = setTimeout(() => {
        const ids = opcionesComparador(s0.comparar, perfilAnswers(s0.comparar)).ids;
        elegirCmp(productoMvp(ids[0]), ids, stepIdx);
      }, 2000);
      return () => clearTimeout(t0);
    }
    if (!autoOn || ended || running || !opts || !opts.length) return undefined;
    const s = mc.steps[stepIdx];
    const t = setTimeout(() => {
      if (s && s.check && checks.some((c) => !c)) {
        setChecks(s.check.map(() => true));
        return;
      }
      choose(opts[0]);
    }, AUTO_MS);
    return () => clearTimeout(t);
  }, [autoOn, ended, running, opts, checks, stepIdx, mc, choose, cmpSel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al aparecer una tarjeta, scroll a su INICIO (una sola vez); si no, al último turno
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let ancla = null;
    for (let j = events.length - 1; j >= 0; j -= 1) {
      const e = events[j];
      if (ANCLAS.includes(e.type) || (e.type === 'perfil' && pfCmp[e.key])) { ancla = e.key; break; }
      if (e.type !== 'sys') break;
    }
    if (ancla) {
      if (anclaRef.current === ancla) return;
      anclaRef.current = ancla;
      if (ancla.startsWith('pf')) return; // el comparador del perfil hace su propio scroll
      const t = el.querySelector(`[data-evkey="${ancla}"]`);
      if (t) el.scrollTo({ top: el.scrollTop + t.getBoundingClientRect().top - el.getBoundingClientRect().top - 8, behavior: 'smooth' });
      return;
    }
    anclaRef.current = null;
    el.scrollTo({ top: el.scrollHeight, behavior: autoOn ? 'auto' : 'smooth' });
  }, [events, opts, checks, advisor, ended, autoOn, pfTick, pfCmp]);

  // Sesión identificada por el WhatsApp del lead (agentes.md §8b) · localStorage
  // Cada turno persiste el estado completo (journey, caso, paso, lead, log con ts).
  useEffect(() => {
    saveSession({ journey: fromJ1 ? 1 : 2, caso: caseId, paso: stepIdx, ended, lead: session.lead, log });
  }, [log, stepIdx, ended, caseId, fromJ1]);

  // Reiniciar sin recargar: conserva los overrides AC12 (solo la recarga de página vuelve al catálogo)
  const reset = useCallback(
    (autoMode) => {
      genRef.current += 1;
      keyRef.current = 0;
      setLog(fromJ1 ? session.j1log.filter(Boolean) : []);
      setEvents(fromJ1 ? introJ1('j1r') : []);
      setPfTexto(null);
      setCmpSel({});
      setPfCmp({});
      setSheet(false);
      setOpts(null);
      setStepIdx(null);
      setStrike(0);
      setChecks([]);
      setAdvisor(null);
      setAdvisorLock(false);
      setEnded(false);
      setRunning(false);
      setShowLog(false);
      setFuente(null);
      setHl(null);
      setLibreInput('');
      setLibreBusy(false);
      if (autoMode != null) setAutoOn(autoMode);
      runStep(startIdx);
    },
    [fromJ1, runStep, startIdx, introJ1],
  );

  const applyPrima = useCallback(
    (min, max) => {
      session.catalogOverrides = { ...(session.catalogOverrides || {}), 'MKT-003': { prima_min: min, prima_max: max } };
      setOvTick((t) => t + 1);
      if (caseId === 'A') reset();
    },
    [caseId, reset],
  );

  const primaEditor = useMemo(() => {
    const base = catalogo.productos.find((p) => p.id === 'MKT-003');
    const ov = session.catalogOverrides && session.catalogOverrides['MKT-003'];
    return {
      id: 'MKT-003',
      min: ov ? ov.prima_min : base.prima_min,
      max: ov ? ov.prima_max : base.prima_max,
      editada: Boolean(ov),
    };
  }, [ovTick]);

  const cur = stepIdx != null ? mc.steps[stepIdx] : null;
  const gated = Boolean(cur && cur.check && checks.some((c) => !c));

  // "¿de dónde sale esto?": abre Vista Junta y resalta SU renglón del log (invariante 3)
  const openFuente = (f) => {
    setFuente(f);
    const tokens = String(f).match(/(MKT-\d+|R\d+)/g) || [];
    const row = log.find((r) => tokens.some((t) => (r.regla || '').includes(t) || (r.fila || '').includes(t)));
    setHl(row ? row.turno : null);
    setShowLog(true);
  };

  const renderEvent = (e) => {
    switch (e.type) {
      case 'typing':
        return <Typing key={e.key} />;
      case 'pago':
        return (
          <div key={e.key} data-evkey={e.key}>
            <CierrePago
              plan={e.plan}
              locked={ended || advisorLock}
              onAsesor={() => { const nx = nextLinear(mc, e.step); if (nx === -1) terminar(); else runStep(nx); }}
              onCambiar={() => setEvents((ev) => {
                const at = ev.findIndex((x) => x.key === e.key);
                return [...ev.slice(0, Math.max(0, at - 1)), { type: 'bubble', from: 'bot', html: 'Claro. Aquí están de nuevo sus opciones; elija otro plan o valor.', key: k('b') }, { type: 'perfil', ramo: mc.caso.ramo, step: e.step, previas: e.answers, key: k('pf') }];
              })}
              onPagado={(ref, forma) => {
                setLog((lg) => [...lg, { turno: lg.length + 1, journey: 2, strike: mc.steps[e.step].strike, agente: 'Cierre', regla: 'Plan cerrado · sin humano (S13)', fila: 'fichas-productos', dec: 'pasa', txt: `Pagó ${e.plan.producto} · ${e.plan.label} · ${forma} · solicitud ${ref}`, ts: new Date().toISOString() }]);
                setEvents((ev) => [...ev, { type: 'bubble', from: 'bot', html: `Listo, quedó en firme. La aseguradora le envía la póliza y el comprobante a su WhatsApp. Número de solicitud: <b>${ref}</b>.`, key: k('b') }]);
                terminar();
              }}
            />
          </div>
        );
      case 'bubble':
        return <Bubble key={e.key} from={e.from} html={e.html} agente={e.agente} wa={waMode} />;
      case 'card':
        return <div key={e.key} data-evkey={e.key}><DataCard card={e.card} onFuente={openFuente} /></div>;
      case 'cmpmvp': {
        const ans = perfilAnswers(e.ramo);
        const op = opcionesComparador(e.ramo, ans);
        const n = op.ids.length;
        return (
          <div key={e.key} data-evkey={e.key}>
            <ComparadorMVP
              ids={op.ids}
              extra={op.extra}
              answers={ans}
              tu={!usted}
              titulo={`Tus ${n} opciones para el ${ans.carro || 'carro'}`}
              sub={ans.ciudad ? `Para manejar en ${ans.ciudad}` : null}
              elegido={cmpSel[e.step] || null}
              locked={e.step !== stepIdx || ended || advisorLock || Boolean(cmpSel[e.step]) || running}
              onElegir={(prod) => elegirCmp(prod, op.ids, e.step)}
              onAsesor={pedirAsesor}
            />
          </div>
        );
      }
      case 'cmp':
        return <Comparator key={e.key} ids={e.ids} resolver={resolver} onFuente={openFuente} />;
      case 'check':
        return (
          <div key={e.key} data-evkey={e.key}>
          <Checklist
            titulo={e.titulo}
            sub={e.sub}
            usted={usted}
            items={e.items}
            done={e.step === stepIdx ? checks : e.items.map(() => true)}
            onCheck={(n) => setChecks((cs) => cs.map((c, i) => (i === n ? true : c)))}
            locked={ended || advisorLock}
            onAsesor={(texto) => { dudaRef.current = String(texto).replace(/<[^>]+>/g, ''); pedirAsesor(); }}
          />
          </div>
        );
      case 'perfil':
        return (
          <Perfilador
            key={e.key}
            ramo={e.ramo}
            set={mc.steps[e.step].perfilSet}
            soloPreguntas={Boolean(mc.steps[e.step].perfilSolo)}
            fijos={mc.steps[e.step].fijos || []}
            previas={e.previas || previasDeLead(session.lead)}
            pre={mc.steps[e.step].perfilPre || {}}
            chat
            registrarTexto={setPfTexto}
            registrarPendiente={setPfPend}
            usted={usted}
            onProgress={perfilParcial}
            onCambio={() => setPfTick((t) => t + 1)}
            auto={autoOn}
            autoAnswers={mc.steps[e.step].perfilAuto}
            locked={e.step !== stepIdx || ended || advisorLock}
            onCmp={() => { setPfCmp((c) => ({ ...c, [e.key]: true })); if (!autoOn) setOpts(mc.steps[e.step].opts || []); }}
            onDone={(r) => perfilListo(r, e.step)}
          />
        );
      case 'advisor':
        return (
          <AdvisorPanel
            key={e.key}
            data={advisor ? advisor.data : null}
            canal={advisor ? advisor.canal : null}
            lead={session.lead}
            usted={usted}
            envios={caseId === 'A' && stepIdx != null && stepIdx >= mc.byId.a_hand && stepIdx <= mc.byId.a_fin_tel ? {
              docs: [
                ['Póliza', () => pdfPoliza('A', session.lead), 'btn-doc-poliza'],
                ['Condiciones generales', () => pdfCondiciones('A'), 'btn-doc-condiciones'],
                ['Plan de pagos', () => pdfPlanPagos('A', pay.plan, pay.cuotas), 'btn-doc-plan'],
                ['Link de pago', () => setPay((pp) => ({ ...pp, open: true, ref: null })), 'btn-link-pago'],
              ],
            } : null}
          />
        );
      case 'sys':
        return (
          <div key={e.key} className="sys-note" data-testid="sys-note">
            {e.text}
          </div>
        );
      case 'libre-cta':
        return (
          <div key={e.key} className="libre-cta-row">
            <button type="button" className="btn-asesor" onClick={pedirAsesor} data-testid="libre-cta-asesor">
              <IconAdvisor size={16} color="#FFFFFF" /> Hablar con un asesor
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`conv ${waMode ? 'wa' : ''}`} data-testid="chat-view">
      <Header title={waMode ? `${marca.nombre} · Asistente` : mc.caso.nombre} onHome={onHome}>
        {waMode ? <span className="wa-status" data-testid="wa-status">en línea</span> : null}
        <button
          type="button"
          className={`btn-ghost ${waMode ? 'on' : ''}`}
          onClick={() => setWaMode((w) => !w)}
          data-testid="btn-wa-toggle"
          title="Reviste el chat con estética WhatsApp"
        >
          <IconChat size={15} /> <span className="btn-label">{waMode ? 'Estándar' : 'WhatsApp'}</span>
        </button>
        <div className="modo-pres">
          <button
            type="button"
            className={`btn-ghost modo-pres-btn ${menu ? 'on' : ''}`}
            onClick={() => setMenu((m) => !m)}
            data-testid="btn-modo-presentacion"
            aria-label="Modo presentación"
            title="Modo presentación"
          >
            <IconPlay size={14} />
          </button>
          {menu ? (
            <div className="modo-pres-menu" data-testid="modo-presentacion-menu">
              <span className="modo-pres-k">Modo presentación</span>
              <button
                type="button"
                className={autoOn ? 'on' : ''}
                onClick={() => { setAutoOn((x) => !x); setMenu(false); }}
                data-testid="btn-auto"
                title="Corre el caso solo: primera opción"
              >
                <IconPlay size={15} /> {autoOn ? 'Detener auto' : 'Auto'}
              </button>
              <button type="button" onClick={() => { reset(); setMenu(false); }} data-testid="btn-reiniciar">
                <IconReset size={15} /> Reiniciar
              </button>
              <button
                type="button"
                onClick={() => { setFuente(null); setHl(null); setShowLog((x) => !x); setMenu(false); }}
                data-testid="btn-vista-junta"
              >
                <IconDoc size={15} /> Vista Junta
              </button>
              <button type="button" onClick={() => { window.location.hash = '#matriz'; }} data-testid="menu-matriz">
                <IconDoc size={15} /> Matriz de riesgo
              </button>
              <button type="button" onClick={() => { resetSession(); window.location.hash = '#V'; }} data-testid="menu-caso-vida">
                <IconArrowRight size={15} /> Ir al caso Vida
              </button>
              <button type="button" onClick={() => { resetSession(); window.location.hash = '#S'; }} data-testid="menu-caso-salud">
                <IconArrowRight size={15} /> Ir al caso Salud
              </button>
            </div>
          ) : null}
        </div>
      </Header>

      <div className="strike-bar" data-testid="strike-stepper">
        {etapas.map((s, i) => (
          <span key={s} className={`strike-dot ${i < strike ? 'done' : ''} ${i === strike ? 'now' : ''}`} title={s}>
            <i />
            <em>{s}</em>
          </span>
        ))}
      </div>

      <main className="conv-scroll" ref={scrollRef}>
        <div className="conv-col">
          <div className="conv-intro">
            <ProtectionRing filled={Math.min(strike + 1, 8)} size={92} />
            <p data-testid="conv-intro-text">
              Ejemplo con datos ficticios. Toca cualquier cifra para ver de dónde sale.
            </p>
          </div>
          {events.map(renderEvent)}
          {ended ? (
            <div className="end-panel" data-testid="end-panel">
              <strong>Resumen de la conversación · {log.length} pasos</strong>
              <span>Exporta el recorrido o míralo de nuevo en automático.</span>
              <div className="end-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setFuente(null);
                    setShowLog(true);
                  }}
                  data-testid="btn-ver-log"
                >
                  <IconDoc size={15} /> Abrir Vista Junta
                </button>
                <button type="button" className="btn-ghost" onClick={() => reset(true)} data-testid="btn-repetir-auto">
                  <IconPlay size={15} /> Ver de nuevo en automático
                </button>
              </div>
              {sinCierre || caseId === 'A' ? null : (<>
              <div className="cierre-block" data-testid="cierre-documentos">
                <span className="cierre-k">Resumen para revisar con tu asesor (ejemplo)</span>
                <div className="cierre-docs">
                  {caseId !== 'B' ? (
                    <button type="button" className="btn-doc" onClick={() => pdfPoliza(caseId, session.lead)} data-testid="btn-doc-poliza">
                      <IconDoc size={15} /> Póliza
                    </button>
                  ) : null}
                  <button type="button" className="btn-doc" onClick={() => pdfCondiciones(caseId)} data-testid="btn-doc-condiciones">
                    <IconDoc size={15} /> Condiciones generales
                  </button>
                  {caseId !== 'B' ? (
                    <button type="button" className="btn-doc" onClick={() => pdfPlanPagos(caseId, pay.plan, pay.cuotas)} data-testid="btn-doc-plan">
                      <IconDoc size={15} /> Plan de pagos
                    </button>
                  ) : null}
                </div>
              </div>
              {caseId === 'C' ? (
                <div className="cierre-block" data-testid="cierre-pago-c">
                  <span className="cierre-k">Cierre por llamada</span>
                  <p className="cierre-nota" data-testid="cierre-nota-c">
                    En hogar no hay link de pago todavía: el asesor confirma primero con usted el valor asegurado y revisa la póliza de la copropiedad. La conversación queda en la llamada agendada.
                  </p>
                </div>
              ) : caseId === 'B' ? null : (
                <div className="cierre-block" data-testid="cierre-pago">
                  <span className="cierre-k">Así se verá el link que te envía el asesor (ejemplo)</span>
                  <button type="button" className="btn-accion" onClick={() => setPay((p) => ({ ...p, open: true, ref: null }))} data-testid="btn-link-pago">
                    Abrir link de pago
                  </button>
                </div>
              )}
              </>)}
            </div>
          ) : null}
        </div>
      </main>

      <footer className="composer">
        {gated && !ended ? (
          <p className="gated-hint" data-testid="gated-hint">
            {usted ? 'Marque «Me queda claro» en cada una para seguir.' : 'Marca «Me queda claro» en cada una para seguir.'}
          </p>
        ) : null}
        <Opts opts={opts} visible={!gated && (!ended || advisorLock)} onChoose={choose} />
        {histRef.current.length > 1 && !running && !advisorLock && !(stepIdx != null && mc.steps[stepIdx].perfil && !ended) ? (
          <button type="button" className="btn-atras" onClick={atras} data-testid="btn-atras">
            {usted ? '← Atrás: cambiar mi respuesta anterior' : '← Atrás: cambiar mi respuesta anterior'}
          </button>
        ) : null}
        <form className="libre" onSubmit={preguntarLibre} data-testid="libre-form">
          <input
            value={libreInput}
            onChange={(e) => setLibreInput(e.target.value)}
            placeholder={pfTexto ? pfTexto.ph : 'Escribe tu pregunta… (respondo solo con la base)'}
            disabled={libreBusy}
            className={pfTexto ? 'espera' : ''}
            data-testid="libre-input"
          />
          <button
            type="submit"
            className="libre-send"
            disabled={libreBusy || !libreInput.trim()}
            data-testid="libre-enviar"
            aria-label="Enviar pregunta"
          >
            <IconArrowRight size={17} />
          </button>
        </form>
        <div className="composer-row">
          <button type="button" className="btn-asesor" onClick={pedirAsesor} data-testid="btn-asesor">
            <IconAdvisor size={17} color="#2F6B4F" /> Hablar con un asesor
          </button>
        </div>
      </footer>

      {showLog ? (
        <EvidenceLog
          log={log}
          fuente={fuente}
          hl={hl}
          lead={session.lead}
          advisor={advisor}
          caso={caseId}
          primaEditor={primaEditor}
          onApplyPrima={applyPrima}
          onClose={() => {
            setShowLog(false);
            setHl(null);
          }}
        />
      ) : null}

      {sheet ? (
        <AsesorSheet
          usted={usted}
          onConfirm={confirmarAsesor}
          onVoz={() => { setSheet(false); setVoz(true); }}
          onClose={() => setSheet(false)}
        />
      ) : null}
      {voz ? (
        <VozLlamada
          marca={marca}
          lead={{ nombre: (session.lead && session.lead.nombre) || '', necesidad: mc.caso.nombre, productos: [mc.caso.producto] }}
          onClose={() => setVoz(false)}
        />
      ) : null}

      {pay.open ? (
        <div className="pay-overlay" data-testid="pay-modal" role="dialog" aria-modal="true">
          <div className="pay-card">
            {(() => {
              const pc = primaCaso(caseId);
              const mensual = pc.min || 0;
              const anual = pc.anual || mensual * 12;
              const n = pay.cuotas || MAX_CUOTAS;
              const cuota = Math.round(anual / n);
              const montoPlan = pay.plan === 'contado'
                ? `$${anual.toLocaleString('es-CO')}`
                : `${n === 1 ? '1 cuota' : `${n} cuotas`} de $${cuota.toLocaleString('es-CO')}`;
              return pay.ref ? (
                <>
                  <div className="pay-ok" data-testid="pay-aprobado">
                    <div className="pay-ok-badge">Pago aprobado</div>
                    <p>Referencia <b>{pay.ref}</b></p>
                    <p className="pay-sub">Simulado · no se realizó ningún cobro real. La póliza queda lista para emisión por el asesor.</p>
                  </div>
                  <button type="button" className="btn-ghost" onClick={() => setPay({ open: false, plan: pay.plan, cuotas: pay.cuotas, ref: null })} data-testid="pay-cerrar">Cerrar</button>
                </>
              ) : (
                <>
                  <span className="pay-k">Link de pago · pasarela simulada</span>
                  <h3>{pc.nombre || 'Póliza'}</h3>
                  <p className="pay-monto" data-testid="pay-monto">{montoPlan}<small>{pay.plan === 'contado' ? `Total anual · ${pc.origen}` : `Total anual $${anual.toLocaleString('es-CO')} dividido en ${n === 1 ? '1 cuota' : `${n} cuotas`}, sin intereses de financiación; si la aseguradora cobra financiación, el asesor la confirma · ${pc.origen}`}</small></p>
                  <div className="pay-plan">
                    <button type="button" className={pay.plan === 'contado' ? 'on' : ''} onClick={() => setPay((p) => ({ ...p, plan: 'contado' }))} data-testid="pay-plan-contado">Contado</button>
                    <button type="button" className={pay.plan === 'cuotas' ? 'on' : ''} onClick={() => setPay((p) => ({ ...p, plan: 'cuotas' }))} data-testid="pay-plan-cuotas">En cuotas</button>
                  </div>
                  {pay.plan === 'cuotas' ? (
                    <label className="pay-cuotas">
                      Número de cuotas
                      <select value={n} onChange={(e) => setPay((p) => ({ ...p, cuotas: Number(e.target.value) }))} data-testid="pay-cuotas">
                        {Array.from({ length: MAX_CUOTAS }, (_, i) => i + 1).map((k) => <option key={k} value={k}>{k === 1 ? '1 cuota' : `${k} cuotas`}</option>)}
                      </select>
                      <small>De 1 a {MAX_CUOTAS}</small>
                    </label>
                  ) : null}
                  {pay.plan === 'cuotas' ? (
                    <table className="pay-tabla" data-testid="pay-plan-cuotas-tabla">
                      <thead><tr><th>Cuota</th><th>Vence</th><th>Valor</th></tr></thead>
                      <tbody>
                        {planCuotas(anual, n).map((c) => (
                          <tr key={c.i}><td>{c.i} de {c.n}</td><td>{fechaCorta(c.vence)}</td><td>${c.valor.toLocaleString('es-CO')}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                  <button
                    type="button"
                    className="btn-accion pay-pagar"
                    onClick={() => setPay((p) => ({ ...p, ref: `MBM-PAY-${Math.floor(100000 + Math.random() * 899999)}` }))}
                    data-testid="pay-confirmar"
                  >
                    Pagar {pay.plan === 'contado' ? 'de contado' : n === 1 ? 'en 1 cuota' : `en ${n} cuotas`}
                  </button>
                  <button type="button" className="pay-cancelar" onClick={() => setPay({ open: false, plan: pay.plan, cuotas: pay.cuotas, ref: null })} data-testid="pay-cancelar">Cancelar</button>
                  <p className="pay-sub">Pasarela de demostración: no cobra.</p>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}
    </div>
  );
}
